/* tslint:disable:max-file-line-count */
/* tslint:disable:no-shadowed-variable */
import styled from '@emotion/styled';
import { InlineAlert } from '@travelnest/hui';
import * as React from 'react';
import { useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useBreakpoint from 'use-breakpoint';

import { Fonts } from '@travelnest/merlin.fonts';
import { DidYouKnow } from '@travelnest/merlin.info-panels';
import { Loader } from '@travelnest/merlin.loader';
import { Spacer } from '@travelnest/merlin.spacer';
import { IPricingOverride, SMART_RATE } from '../../../data/entities/pricing';
import { IProperty } from '../../../data/entities/property';
import { actions } from '../../../data/store';
import { isAdmin } from '../../../entities/admin/selectors';
import { currentUserId } from '../../../entities/auth/session/selectors';
import { idOrLegacyId } from '../../../entities/properties/selectors';
import { useCustomerExperiments } from '../../../experiment';
import { logging } from '../../../logging';
import { IThunkDispatch } from '../../../store/middleware';
import { IState } from '../../../store/reducer';
import {
	Calendar, getFirstDayOfMonth, getLastDayOfMonth, MonthPagination
} from '../../components/calendar';
import { ICalendarEvent } from '../../components/calendar/types';
import { setCalendarPersistence } from './actions';
import { dateEquals, incrementDate, rangesToDates, stripDuplicateDates } from './calendar-helpers';
import { CalendarSettings } from './CalendarSettings';
import { CallToActionDrawerPP } from './CallToActionDrawerPP';
import { ContextualActions } from './ContextualActions';
import { useEvents, useSmartPricing } from './hooks';
import { getOverridesPayload } from './overrides-helpers';
import { SmartPricingCardPP } from './SmartPricingCardPP';
export const BREAKPOINTS = { mobile: 0, desktop: 1024 };

export interface ICalendarBundleProps {
	loadingProperties: boolean;
	propertyId?: string | number;
	multiPropertyMode?: boolean;
	initialMonth?: Date;
	setShowHeader?: (show: boolean) => any;
	customerId?: string;
}

const ReflowWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const CalendarWrapper = styled.div`
`;

const CalendarContextWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const MonthPaginationWrapper = styled.div<{ multiPropertyMode?: boolean }>`
	display: flex;
	flex-direction: column;

	@media only screen and (min-width: 1180px) {
		flex-direction: row;
		height: ${(props) => props.multiPropertyMode ? '60px' : ''};
	}

	@media only screen and (min-width: 768px) and (max-width: 1024px) {
		flex-direction: row;
		height: ${(props) => props.multiPropertyMode ? '60px' : ''};
	}
`;

const SmartPricingWarningWrapper = styled.div<{ multiPropertyMode?: boolean }>`
	@media only screen and (min-width: 1180px) {
		margin-left: 20px;
		position: relative;
		bottom: ${(props) => props.multiPropertyMode ? '68px' : ''};
	}

	@media only screen and (min-width: 768px) and (max-width: 1024px) {
		margin-left: 20px;
		position: relative;
		bottom: ${(props) => props.multiPropertyMode ? '68px' : ''};
	}
`;

const CalendarLoader = () => {
	return <div style={{ height: '50px', marginTop: '20px' }}>
		<Loader />
	</div>;
};

export const eventForDate = (date: Date, events: ICalendarEvent[]): ICalendarEvent | undefined => {
	const containingDate = events.filter((e) => date >= e.start && date < e.end);
	return containingDate.length > 0 ? containingDate[0] : undefined;
};

export const eventsForMonth = (month: Date, events: ICalendarEvent[]): ICalendarEvent[] => {
	const monthStart = getFirstDayOfMonth(month);
	const monthEnd = getLastDayOfMonth(month);

	return events.filter(({ start, end }) => start <= monthEnd && end >= monthStart);
};

export const CalendarBundlePP: React.FC<ICalendarBundleProps> = (
	{ propertyId, setShowHeader, loadingProperties, initialMonth, multiPropertyMode }
) => {
	const navigate = useNavigate();
	const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
	const { getState, dispatch }: { getState: () => IState, dispatch: IThunkDispatch } = useStore();
	const canEditAvailability = getState().entities.flags.userFeatures.canEditAvailability;
	const adminUser = isAdmin(getState());

	// This returns a legacy ID if one exists for the property, and the numeric ID if not.
	// Required when downstream services (eg availability) expect a legacy ID if one exists for a property.
	const normalisedPropertyId = propertyId !== undefined ? idOrLegacyId(propertyId!) : undefined;

	const { events, loading: eventsLoading } = useEvents(normalisedPropertyId);
	const { pricing, loading: pricingLoading, pricingType, currency } = useSmartPricing(propertyId!);

	const property: IProperty | undefined = getState().data.properties.byId[propertyId!];

	const [currentMonth, setCurrentMonth] = React.useState<Date>(() => {
		const initial = initialMonth !== undefined ? initialMonth : new Date();
		return new Date(initial.getFullYear(), initial.getMonth(), initial.getDate());
	});
	const [selectedDates, setSelectedDates] = React.useState<Date[]>([]);
	const [selectedEvents, setSelectedEvents] = React.useState<ICalendarEvent[]>([]);
	const [selectedAvailableDates, setSelectedAvailableDates] = React.useState<Date[]>([]);

	// These state vars determine whether to show the CTA Drawer and the ContextualActions. We use exactly the same components for both desktop
	// and mobile, so the logic and state transformations get somewhat complicated.
	const [showContextualActions, setShowContextualActions] = React.useState(false);
	// Always shows actions on desktop sidepanel to have the selection info box (rest is hidden with no nights selection)
	const shouldShowActions = showContextualActions || breakpoint === 'desktop';
	const showActions = (show: boolean) => {
		setShowContextualActions(show);
		if (setShowHeader) setShowHeader(show);
	};

	const [resolveEventsLoading, setResolveEventsLoading] = React.useState(false);
	const [deleteEventsLoading, setDeleteEventsLoading] = React.useState(false);

	const customerId = currentUserId(getState());
	const smartPricingExperimentStatus = useCustomerExperiments(customerId);
	const [smartPricingExperimentValue, setSmartPricingExperimentValue] = React.useState<SMART_RATE>();
	React.useEffect(() => {
		if (SMART_RATE[smartPricingExperimentStatus?.smart_pricing_customer]) {
			setSmartPricingExperimentValue(SMART_RATE[smartPricingExperimentStatus?.smart_pricing_customer]);
		}
	}, [smartPricingExperimentStatus]);

	// Hide actions panel when selection is cleared
	React.useEffect(() => {
		if (selectedDates.length === 0 && showContextualActions) {
			showActions(false);
		}
	}, [selectedDates]);

	// Clear selection when new property is selected
	React.useEffect(() => {
		if (!!propertyId) {
			clearSelection();
		}
	}, [propertyId]);

	// Persist selected propertyId & currentMonth via URL query params, means customer can click off
	// to view bookings, then hit back button and see the same view they were looking at previously
	React.useEffect(() => {
		dispatch(setCalendarPersistence({ propertyId, currentMonth }));
	}, [propertyId, currentMonth]);

	const resolveEvents = async (property: string | number | undefined, create: boolean, ranges: Array<[Date, Date]>) => {
		if (property === undefined) return;
		// Portal calendar dates are inclusive, but availability is non-inclusive, so extend by 1 day
		const payload = ranges.map((range) => ({ startOn: range[0], endOn: incrementDate(range[1], 1) }));
		setResolveEventsLoading(true);
		await dispatch(actions.availabilityV3.resolveEvents(property, payload, create ? 'create' : 'delete'));
		setResolveEventsLoading(false);
	};

	const deleteSyncedEvent = async (property: string | number | undefined, event: ICalendarEvent) => {
		if (property === undefined) return;
		setDeleteEventsLoading(true);
		await dispatch(actions.availability.deleteEvent(property, { ...event, property_id: property }));
		setDeleteEventsLoading(false);
	};

	const updateOverridesForDates = async (
		property: string | number | undefined,
		ranges: Array<[Date, Date]>,
		priceSmartOrCustom: 'smart' | 'custom',
		override?: Partial<IPricingOverride>
	) => {
		// if property is undefined, there's an error
		if (property) {
			// upsertByPropertyId action expects property to be a number
			if (typeof property === 'string') property = parseInt(property, 10);

			// pull all existing overrides from Redux state
			const existingOverrides: IPricingOverride[] =
				getState().data.pricingOverrides?.byPropertyId[property] as unknown as IPricingOverride[];

			const payload = getOverridesPayload(existingOverrides, ranges, priceSmartOrCustom, override);

			await dispatch(actions.pricingOverrides.upsertByPropertyId(property, payload as any));
		} else {
			logging.error(`Cannot update overrides as property ID is undefined`);
		}
	};

	const onCalSelectionChange = (dates: Date[]) => {
		// Work out which events are selected and which nights are available (have no associated event);
		// both used to build the actions panel's selected status info.
		const availableDates = [];
		const selectionEvents = [];
		for (const d of dates) {
			const event = eventForDate(d, events);
			if (event !== undefined) {
				selectionEvents.push(event);
			} else {
				availableDates.push(d);
			}
		}
		setSelectedDates(dates);
		setSelectedEvents([...new Set(selectionEvents)]);
		setSelectedAvailableDates(availableDates);
	};

	const clearSelection = () => {
		onCalSelectionChange([]);
	};

	const switchMonth = (direction: 'prev' | 'next') => {
		const newMonth = new Date(currentMonth);
		newMonth.setMonth(currentMonth.getMonth() + (direction === 'prev' ? -1 : 1));
		setCurrentMonth(newMonth);
	};

	return <>
		<Fonts />
		<ReflowWrapper>
			<CalendarWrapper>
				{(eventsLoading || pricingLoading || loadingProperties) ? <CalendarLoader /> :
					<>
						<InlineAlert
							variant={'informative'}
							content={'Select any number of days to update their availability.'}
						/>
						<Spacer spacing={'small'} />
						<MonthPaginationWrapper multiPropertyMode={multiPropertyMode}>
							<MonthPagination
								prev={() => switchMonth('prev')}
								next={() => switchMonth('next')}
								current={currentMonth}
								selectMonth={(nextMonth: Date) => setCurrentMonth(nextMonth)}
							/>
							{!property?.published_version && pricingType?.type === 'automatic' && <SmartPricingWarningWrapper multiPropertyMode={multiPropertyMode}>
								<DidYouKnow
									id="smart-pricing"
									text={'Prices will not be regularly updated until the property is published'}
									colour={{ background: '#F4F6F9', text: '#535D6A' }}
									icon={'/71eb904/assets/icons/calendar/calendaricon.svg'}
								/>
							</SmartPricingWarningWrapper>}
						</MonthPaginationWrapper>
						<div className="calendar-column">
							<Calendar
								events={events}
								selectedDates={selectedDates}
								nightlyPrices={pricing}
								current={currentMonth}
								onDateSelectionChange={onCalSelectionChange}
								goToBooking={
									(eventId: string) => {
										const event = events.find((e) => eventId === e.id);
										if (event!.status === 'booked' && event!.data.managedBy !== 'supercontrol.com') {
											navigate(`/property/${event!.data!.propertyId}/booking/${event!.id}`);
										}
									}
								}
								onSyncedEventClick={(event: ICalendarEvent) => {
									// we use incrementDate here so that we don't select the day the
									// event finishes on the calendar as that day is technically not part
									// of the event
									const datesToSelect = rangesToDates([[event.start, incrementDate(event.end, -1)]]);
									const uniqueDates = stripDuplicateDates([...selectedDates, ...datesToSelect]);
									onCalSelectionChange(uniqueDates);
								}}
							/>
							<Spacer spacing="large" />
							{
								canEditAvailability &&
								<div className="sidebar">
									<CalendarContextWrapper>
										<Spacer spacing="standard" />
										<CallToActionDrawerPP
											selectedNights={selectedDates}
											hide={showContextualActions}
											onCancel={() => {
												clearSelection();
											}}
											showContextualActions={() => showActions(true)} />
									</CalendarContextWrapper>
								</div>
							}
						</div>
						{
							!!smartPricingExperimentValue &&
							<>
								<SmartPricingCardPP propertyId={propertyId!} smartPricingEnabled={pricingType?.type === 'automatic'} />
								<Spacer spacing="large" />
							</>
						}
						{
							canEditAvailability &&
							<CalendarSettings
								propertyId={propertyId}
								breakpoint={breakpoint}
								isAdmin={adminUser}
								onCalSelectionChange={onCalSelectionChange}
								setCurrentMonth={setCurrentMonth}
							/>
						}
						{shouldShowActions && <ContextualActions
							pricingType={pricingType?.type}
							currency={currency}
							breakpoint={breakpoint}
							selectedDates={selectedDates}
							selectedEvents={selectedEvents}
							selectedAvailableDates={selectedAvailableDates}
							onSelectionChange={setSelectedDates}
							onConfirmLoading={resolveEventsLoading || deleteEventsLoading}
							propertyId={normalisedPropertyId || ''}
							onConfirm={async (block, ranges, isOverride, override) => {
								// check if a single range has been selected
								const isSingleRange = ranges.length === 1;
								// is there an event matching that single range
								const eventMatchingRange = isSingleRange
									? events.find((e) => dateEquals(e.start, ranges[0][0]) && dateEquals(e.end, incrementDate(ranges[0][1], 1)))
									: undefined;
								// check if the possibly matching event is a synced event
								const eventIsSynced = eventMatchingRange?.status === 'synced' ?? false;
								// if the event is synced and we are trying to make those dates available
								if (!block && eventIsSynced) {
									await deleteSyncedEvent(normalisedPropertyId, eventMatchingRange!);
								} else {
									await resolveEvents(normalisedPropertyId, block, ranges);
								}
								if (pricingType?.type === 'automatic' && !block) {
									// For updating overrides, we purposefully send the propertyId variable and not the normalisedPropertyId.
									// This is because the pricing service expects the numeric ID, NOT the legacy one if it exists.
									const pricingType = isOverride ? 'custom' : 'smart';
									const overrides = isOverride ? override : undefined;
									await updateOverridesForDates(propertyId, ranges, pricingType, overrides);
								}
							}}
							clearSelection={() => {
								showActions(false);
								clearSelection();
							}}
							onBack={() => {
								showActions(false);
							}}
							onCancel={() => {
								clearSelection();
								showActions(false);
							}}
							onSuccessfulRequest={() => {
								clearSelection();
								showActions(false);
							}}
						/>}
					</>}
			</CalendarWrapper>
		</ReflowWrapper>
	</>;
};
